import React, { useEffect, useState } from 'react';
import { Building2, Phone, Mail, MapPin, Clock, Save, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { getClinic, updateClinic, Clinic } from '../services/clinicService';
import { SessionUser } from '../types';

interface ClinicSettingsProps {
  user: SessionUser;
}

const ClinicSettings: React.FC<ClinicSettingsProps> = ({ user }) => {
  const [clinic, setClinic] = useState<Clinic | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    hours: '7/24 Açık',
  });

  useEffect(() => {
    const load = async () => {
      if (!user.clinicId) {
        setLoading(false);
        return;
      }
      try {
        const c = await getClinic(user.clinicId);
        if (c) {
          setClinic(c);
          setForm({
            name: c.name || '',
            phone: c.phone || '',
            email: c.email || '',
            address: c.address || '',
            hours: c.hours || '7/24 Açık',
          });
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user.clinicId]);

  const handleSave = async () => {
    if (!user.clinicId) {
      toast.error('Klinik bilgisi bulunamadı');
      return;
    }
    if (!form.name.trim()) {
      toast.error('Klinik adı zorunludur');
      return;
    }
    setSaving(true);
    try {
      const updated = await updateClinic(user.clinicId, form);
      if (updated) {
        setClinic(updated);
        toast.success('Klinik bilgileri güncellendi');
      }
    } catch (e: any) {
      toast.error(e?.message || 'Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container py-8 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
      </div>
    );
  }

  if (!user.clinicId) {
    return (
      <div className="page-container py-8">
        <div className="card text-center py-12">
          <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600">Klinik bilgisi bulunamadı.</p>
          <p className="text-sm text-gray-400 mt-2">Lütfen sistem yöneticinizle iletişime geçin.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container py-6 lg:py-8">
      {/* Header */}
      <div className="page-title-row">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white shadow-md">
              <Building2 className="w-5 h-5" />
            </div>
            Klinik Ayarları
          </h1>
          <p className="text-sm text-gray-500 mt-2">
            Bu bilgiler hasta sahiplerinin mobil uygulamasında görünür.
          </p>
        </div>
      </div>

      {/* Form Card */}
      <div className="card mt-6 max-w-3xl">
        <div className="space-y-5">
          {/* Klinik Adı */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Klinik Adı *
            </label>
            <div className="relative">
              <Building2 className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input pl-10"
                placeholder="Örn: Pati Veteriner Kliniği"
              />
            </div>
          </div>

          {/* Telefon */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Telefon Numarası
            </label>
            <div className="relative">
              <Phone className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="input pl-10"
                placeholder="+90 555 000 0000"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Hasta sahipleri buradan sizi arayabilir veya WhatsApp'tan ulaşabilir.
            </p>
          </div>

          {/* E-posta */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              E-posta
            </label>
            <div className="relative">
              <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input pl-10"
                placeholder="info@klinik.com"
              />
            </div>
          </div>

          {/* Adres */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Adres
            </label>
            <div className="relative">
              <MapPin className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
              <textarea
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="input pl-10 min-h-[80px]"
                rows={2}
                placeholder="Klinik adresiniz"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Hasta sahipleri buradan Google Haritalar'da konumu açabilir.
            </p>
          </div>

          {/* Çalışma Saatleri */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Çalışma Saatleri
            </label>
            <div className="relative">
              <Clock className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={form.hours}
                onChange={(e) => setForm({ ...form, hours: e.target.value })}
                className="input pl-10"
                placeholder="7/24 Açık veya Pzt-Cmt 09:00-18:00"
              />
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {['7/24 Açık', 'Pzt-Cmt 09:00-18:00', 'Pzt-Cmt 09:00-19:00', 'Pzt-Pzr 10:00-20:00'].map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setForm({ ...form, hours: preset })}
                  className={`px-3 py-1 text-xs rounded-full border transition-all ${
                    form.hours === preset
                      ? 'bg-teal-500 text-white border-teal-500'
                      : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {preset}
                </button>
              ))}
            </div>
          </div>

          {/* Save */}
          <div className="pt-4 border-t border-gray-100">
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn btn-primary inline-flex items-center gap-2 disabled:opacity-60"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Bilgileri Kaydet
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="card mt-6 max-w-3xl bg-teal-50/50 border-teal-100">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-teal-500 text-white flex items-center justify-center flex-shrink-0">
            <Building2 className="w-4 h-4" />
          </div>
          <div className="text-sm text-teal-900">
            <p className="font-semibold mb-1">Mobil uygulama bilgileri</p>
            <p className="text-teal-700 leading-relaxed">
              Buradaki bilgiler, hasta sahiplerinin <strong>KlinikVet mobil uygulamasında</strong> "İletişim" bölümünde görünür.
              Telefon, WhatsApp, e-posta ve harita bağlantıları otomatik oluşturulur.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicSettings;
